import { apiErrorMessage } from "@/lib/apiErrorMessage";
import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import i18n from "@/i18n";

// Shared field helper (kept lightweight to match wizard styling).
const inputCls = "h-10 w-full px-3 rounded-md border border-neutral-300 text-sm focus:border-neutral-900 focus:outline-none";

export function Field({ label, required, children, hint, testId }) {
    return (
        <div data-testid={testId}>
            <label className="text-xs font-semibold text-neutral-600">
                {label} {required && <span className="text-rose-600">*</span>}
            </label>
            <div className="mt-1">{children}</div>
            {hint && <div className="text-[11px] text-neutral-500 mt-0.5">{hint}</div>}
        </div>
    );
}

// ── New Restaurant ─────────────────────────────────────────────
export function NewRestaurantDialog({ onClose, onCreated }) {
    const [companies, setCompanies] = useState([]);
    const [f, setF] = useState({
        company_id: "", name: "", city: "", country: "",
        time_zone: "Europe/Helsinki", currency: "EUR",
        language: "en", address: "", notes: "", active: true,
    });
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        api.get("/admin/companies").then(({ data }) => setCompanies(data || []));
    }, []);
    const upd = (p) => setF((x) => ({ ...x, ...p }));
    const canSubmit = f.company_id && f.name.trim().length > 1 && f.city.trim().length > 1;
    const submit = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/restaurants", f);
            toast.success(i18n.t("admin.l.Restaurant_created", "Restaurant created"));
            onCreated?.(data);
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally { setBusy(false); }
    };
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-xl" data-testid="new-restaurant-dialog">
                <DialogHeader>
                    <DialogTitle>{i18n.t("admin.l.New_Restaurant", "New Restaurant")}</DialogTitle>
                    <DialogDescription>
                        {i18n.t("admin.l.Adds_a_restaurant_under_the_selected_company_Active_by_default", "Adds a restaurant under the selected company. Active by default.")}
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-3 md:grid-cols-2">
                    <div className="md:col-span-2">
                        <Field label={i18n.t("admin.l.Company", "Company")} required testId="nr-company-field">
                            <select data-testid="nr-company" className={inputCls}
                                value={f.company_id} onChange={(e) => upd({ company_id: e.target.value })}>
                                <option value="">{i18n.t("admin.l.Select_company", "— Select company —")}</option>
                                {companies.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </Field>
                    </div>
                    <Field label={i18n.t("admin.l.Restaurant_name", "Restaurant name")} required testId="nr-name-field">
                        <input data-testid="nr-name" className={inputCls}
                            value={f.name} onChange={(e) => upd({ name: e.target.value })} />
                    </Field>
                    <Field label={i18n.t("admin.l.City", "City")} required testId="nr-city-field">
                        <input data-testid="nr-city" className={inputCls}
                            value={f.city} onChange={(e) => upd({ city: e.target.value })} />
                    </Field>
                    <Field label={i18n.t("admin.l.Country", "Country")} testId="nr-country-field">
                        <input data-testid="nr-country" className={inputCls}
                            value={f.country} onChange={(e) => upd({ country: e.target.value })} />
                    </Field>
                    <Field label={i18n.t("admin.l.Time_zone", "Time zone")} testId="nr-tz-field">
                        <input data-testid="nr-tz" className={inputCls}
                            value={f.time_zone} onChange={(e) => upd({ time_zone: e.target.value })} />
                    </Field>
                    <div className="md:col-span-2">
                        <Field label={i18n.t("admin.l.Address", "Address")} testId="nr-address-field">
                            <input data-testid="nr-address" className={inputCls}
                                value={f.address} onChange={(e) => upd({ address: e.target.value })} />
                        </Field>
                    </div>
                    <div className="md:col-span-2 flex items-center gap-2">
                        <input id="nr-active" data-testid="nr-active" type="checkbox"
                            checked={f.active} onChange={(e) => upd({ active: e.target.checked })} />
                        <label htmlFor="nr-active" className="text-sm">{i18n.t("admin.l.Active_on_creation", "Active on creation")}</label>
                    </div>
                </div>
                <DialogActions onClose={onClose}
                    canSubmit={canSubmit} busy={busy} onSubmit={submit}
                    submitLabel={i18n.t("admin.createRestaurant", "Create Restaurant")}
                    submitTestId="nr-submit" />
            </DialogContent>
        </Dialog>
    );
}

// ── New User ────────────────────────────────────────────────────
const ADMIN_ROLES = [
    { value: "super_admin",       get label() { return i18n.t("admin.l.Super_Admin", "Super Admin"); },       restaurant: false },
    { value: "corporate",         get label() { return i18n.t("admin.l.Corporate", "Corporate"); },         restaurant: false },
    { value: "restaurant_admin",  get label() { return i18n.t("admin.l.Restaurant_Admin", "Restaurant Admin"); },  restaurant: true },
    { value: "head_chef",         get label() { return i18n.t("admin.l.Head_Chef", "Head Chef"); },         restaurant: true },
    { value: "kitchen_staff",     get label() { return i18n.t("admin.l.Kitchen_Staff", "Kitchen Staff"); },     restaurant: true },
    { value: "read_only",         get label() { return i18n.t("admin.l.Read_Only", "Read Only"); },         restaurant: true },
];

export function NewUserDialog({ onClose, onCreated }) {
    const [companies, setCompanies] = useState([]);
    const [restaurants, setRestaurants] = useState([]);
    const [f, setF] = useState({
        name: "", email: "", role: "corporate",
        company_id: "", restaurant_id: "", status: "active",
        // Optional manual password — Super Admin owns password lifecycle.
        password: "", password_confirm: "",
    });
    const [busy, setBusy] = useState(false);
    const [tempPass, setTempPass] = useState(null);
    useEffect(() => {
        api.get("/admin/companies").then(({ data }) => setCompanies(data || []));
    }, []);
    useEffect(() => {
        if (!f.company_id) return setRestaurants([]);
        api.get("/admin/restaurants", { params: { company_id: f.company_id } })
            .then(({ data }) => setRestaurants(data || []));
    }, [f.company_id]);
    const upd = (p) => setF((x) => ({ ...x, ...p }));
    const roleDef = ADMIN_ROLES.find((r) => r.value === f.role);
    const isRestaurantLevel = !!roleDef?.restaurant;
    const pwdMismatch = f.password && f.password_confirm && f.password !== f.password_confirm;
    const pwdOk = !f.password || (f.password.length >= 6 && !pwdMismatch);
    const canSubmit = f.name.trim().length > 1
        && f.email.includes("@")
        && (f.role === "super_admin" || f.company_id)
        && (!isRestaurantLevel || f.restaurant_id)
        && pwdOk;
    const submit = async () => {
        setBusy(true);
        try {
            const payload = {
                name: f.name, email: f.email, role: f.role,
                status: f.status,
                company_id: f.role === "super_admin" ? null : f.company_id,
                restaurant_id: isRestaurantLevel ? f.restaurant_id : null,
                password: f.password || null,
            };
            const { data } = await api.post("/admin/users", payload);
            setTempPass({ user: data, name: f.name, email: f.email });
            toast.success(i18n.t("admin.l.User_created", "User created"));
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally { setBusy(false); }
    };
    if (tempPass) {
        const isAdminSet = tempPass.user.temporary_password == null;
        return (
            <Dialog open onOpenChange={() => { onCreated?.(); onClose(); }}>
                <DialogContent className="max-w-md" data-testid="new-user-success">
                    <DialogHeader>
                        <DialogTitle>{i18n.t("admin.l.User_created", "User created")}</DialogTitle>
                        <DialogDescription>
                            {isAdminSet
                                ? i18n.t("admin.l.The_user_can_log_in_with_the_password_you_set", "The user can log in with the password you set.")
                                : i18n.t("admin.l.Save_the_temporary_password_now_it_will_not_be_shown_again", "Save the temporary password now — it will not be shown again.")}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="rounded-md border border-neutral-200 p-3">
                        <div className="text-xs uppercase tracking-wide text-neutral-500">{i18n.t("admin.l.User", "User")}</div>
                        <div className="font-semibold text-sm">{tempPass.name} · {tempPass.email}</div>
                        {isAdminSet ? (
                            <div className="mt-2 rounded-md bg-neutral-100 text-neutral-600 px-3 py-2 text-xs italic"
                                 data-testid="nu-admin-set">
                                {i18n.t("admin.l.Password_set_manually_by_admin_not_shown_here", "Password set manually by admin — not shown here.")}
                            </div>
                        ) : (
                            <div className="mt-2 flex items-center justify-between rounded-md bg-neutral-900 text-white px-3 py-2 font-mono text-sm">
                                <span>{tempPass.user.temporary_password}</span>
                                <button data-testid="nu-copy"
                                    onClick={() => { navigator.clipboard.writeText(tempPass.user.temporary_password); toast.success(i18n.t("admin.l.Copied", "Copied")); }}
                                    className="ml-3 h-7 w-7 grid place-items-center rounded hover:bg-neutral-800"
                                    title={i18n.t("admin.l.Copy", "Copy")}>
                                    <Copy className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="flex justify-end mt-4">
                        <button
                            data-testid="nu-done"
                            onClick={() => { onCreated?.(); onClose(); }}
                            className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold">
                            Done
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-xl" data-testid="new-user-dialog">
                <DialogHeader>
                    <DialogTitle>{i18n.t("admin.l.New_User", "New User")}</DialogTitle>
                    <DialogDescription>
                        {i18n.t("admin.l.A_temporary_password_will_be_generated_and_shown_once", "A temporary password will be generated and shown once.")}
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-3 md:grid-cols-2">
                    <div className="md:col-span-2">
                        <Field label={i18n.t("admin.l.Role", "Role")} required testId="nu-role-field">
                            <select data-testid="nu-role" className={inputCls}
                                value={f.role} onChange={(e) => upd({ role: e.target.value, restaurant_id: "" })}>
                                {ADMIN_ROLES.map((r) => (
                                    <option key={r.value} value={r.value}>{r.label}</option>
                                ))}
                            </select>
                        </Field>
                    </div>
                    {f.role !== "super_admin" && (
                        <div className="md:col-span-2">
                            <Field label={i18n.t("admin.l.Company", "Company")} required testId="nu-company-field">
                                <select data-testid="nu-company" className={inputCls}
                                    value={f.company_id} onChange={(e) => upd({ company_id: e.target.value })}>
                                    <option value="">{i18n.t("admin.l.Select_company", "— Select company —")}</option>
                                    {companies.map((c) => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </select>
                            </Field>
                        </div>
                    )}
                    {isRestaurantLevel && (
                        <div className="md:col-span-2">
                            <Field label={i18n.t("admin.l.Restaurant", "Restaurant")} required testId="nu-restaurant-field">
                                <select data-testid="nu-restaurant" className={inputCls}
                                    value={f.restaurant_id} onChange={(e) => upd({ restaurant_id: e.target.value })}
                                    disabled={!f.company_id}>
                                    <option value="">{i18n.t("admin.l.Select_restaurant", "— Select restaurant —")}</option>
                                    {restaurants.map((r) => (
                                        <option key={r.id} value={r.id}>{r.name}</option>
                                    ))}
                                </select>
                            </Field>
                        </div>
                    )}
                    <Field label={i18n.t("admin.l.Full_name", "Full name")} required testId="nu-name-field">
                        <input data-testid="nu-name" className={inputCls}
                            value={f.name} onChange={(e) => upd({ name: e.target.value })} />
                    </Field>
                    <Field label={i18n.t("admin.l.Email", "Email")} required testId="nu-email-field">
                        <input data-testid="nu-email" type="email" className={inputCls}
                            value={f.email} onChange={(e) => upd({ email: e.target.value })} />
                    </Field>
                    <div className="md:col-span-2">
                        <Field label={i18n.t("admin.l.Status", "Status")} testId="nu-status-field">
                            <select data-testid="nu-status" className={inputCls}
                                value={f.status} onChange={(e) => upd({ status: e.target.value })}>
                                <option value="active">{i18n.t("admin.l.active", "active")}</option>
                                <option value="suspended">{i18n.t("admin.l.suspended", "suspended")}</option>
                                <option value="pending_invite">{i18n.t("admin.l.pending_invite", "pending invite")}</option>
                            </select>
                        </Field>
                    </div>
                    <Field label={i18n.t("admin.l.Password", "Password")} testId="nu-password-field"
                           hint={i18n.t("admin.l.Optional_leave_empty_to_auto_generate_a_temporary_password", "Optional — leave empty to auto-generate a temporary password.")}>
                        <input data-testid="nu-password" type="password" className={inputCls}
                            value={f.password} onChange={(e) => upd({ password: e.target.value })} />
                    </Field>
                    <Field label={i18n.t("admin.l.Confirm_Password", "Confirm Password")} testId="nu-password-confirm-field"
                           hint={pwdMismatch ? i18n.t("admin.l.Passwords_do_not_match", "Passwords do not match.") : ""}>
                        <input data-testid="nu-password-confirm" type="password"
                            className={`${inputCls} ${pwdMismatch ? "border-rose-400" : ""}`}
                            value={f.password_confirm} onChange={(e) => upd({ password_confirm: e.target.value })} />
                    </Field>
                </div>
                <DialogActions onClose={onClose}
                    canSubmit={canSubmit} busy={busy} onSubmit={submit}
                    submitLabel={i18n.t("admin.createUser", "Create User")}
                    submitTestId="nu-submit" />
            </DialogContent>
        </Dialog>
    );
}

// ── New Plan ────────────────────────────────────────────────────
export function NewPlanDialog({ onClose, onCreated }) {
    const [f, setF] = useState({
        name: "", code: "", monthly_price: 0, yearly_price: 0,
        restaurant_limit: 1, user_limit: 5, storage_limit_gb: 5,
        support_level: "standard", status: "active",
    });
    const [busy, setBusy] = useState(false);
    const upd = (p) => setF((x) => ({ ...x, ...p }));
    const canSubmit = f.name.trim().length > 1;
    const submit = async () => {
        setBusy(true);
        try {
            const payload = { ...f, code: (f.code || f.name).toLowerCase().replace(/\s+/g, "_") };
            const { data } = await api.post("/admin/plans", payload);
            toast.success(i18n.t("admin.l.Plan_created", "Plan created"));
            onCreated?.(data);
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally { setBusy(false); }
    };
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-xl" data-testid="new-plan-dialog">
                <DialogHeader>
                    <DialogTitle>{i18n.t("admin.l.New_Plan", "New Plan")}</DialogTitle>
                    <DialogDescription>{i18n.t("admin.l.Defines_the_subscription_tiers_customers_can_select", "Defines the subscription tiers customers can select.")}</DialogDescription>
                </DialogHeader>
                <div className="grid gap-3 md:grid-cols-2">
                    <Field label={i18n.t("admin.l.Plan_name", "Plan name")} required testId="np-name-field">
                        <input data-testid="np-name" className={inputCls}
                            value={f.name} onChange={(e) => upd({ name: e.target.value })} />
                    </Field>
                    <Field label={i18n.t("admin.l.Plan_code", "Plan code")} testId="np-code-field" hint={i18n.t("admin.l.Auto_derived_from_the_name_if_empty", "Auto-derived from the name if empty.")}>
                        <input data-testid="np-code" className={inputCls}
                            value={f.code} onChange={(e) => upd({ code: e.target.value })} />
                    </Field>
                    <Field label={i18n.t("admin.l.Monthly_price_EUR", "Monthly price (€)")} testId="np-monthly-field">
                        <input data-testid="np-monthly" type="number" min="0" step="0.01" className={inputCls}
                            value={f.monthly_price} onChange={(e) => upd({ monthly_price: Number(e.target.value) })} />
                    </Field>
                    <Field label={i18n.t("admin.l.Yearly_price_EUR", "Yearly price (€)")} testId="np-yearly-field">
                        <input data-testid="np-yearly" type="number" min="0" step="0.01" className={inputCls}
                            value={f.yearly_price} onChange={(e) => upd({ yearly_price: Number(e.target.value) })} />
                    </Field>
                    <Field label={i18n.t("admin.l.Restaurant_limit", "Restaurant limit")} testId="np-rest-limit-field">
                        <input data-testid="np-rest-limit" type="number" min="0" step="1" className={inputCls}
                            value={f.restaurant_limit} onChange={(e) => upd({ restaurant_limit: Number(e.target.value) })} />
                    </Field>
                    <Field label={i18n.t("admin.l.User_limit", "User limit")} testId="np-user-limit-field">
                        <input data-testid="np-user-limit" type="number" min="0" step="1" className={inputCls}
                            value={f.user_limit} onChange={(e) => upd({ user_limit: Number(e.target.value) })} />
                    </Field>
                    <Field label={i18n.t("admin.l.Storage_limit_GB", "Storage limit (GB)")} testId="np-storage-field">
                        <input data-testid="np-storage" type="number" min="0" step="1" className={inputCls}
                            value={f.storage_limit_gb} onChange={(e) => upd({ storage_limit_gb: Number(e.target.value) })} />
                    </Field>
                    <Field label={i18n.t("admin.l.Support_level", "Support level")} testId="np-support-field">
                        <select data-testid="np-support" className={inputCls}
                            value={f.support_level} onChange={(e) => upd({ support_level: e.target.value })}>
                            <option value="basic">{i18n.t("admin.l.basic", "basic")}</option>
                            <option value="standard">{i18n.t("admin.l.standard", "standard")}</option>
                            <option value="priority">{i18n.t("admin.l.priority", "priority")}</option>
                        </select>
                    </Field>
                    <div className="md:col-span-2">
                        <Field label={i18n.t("admin.l.Status", "Status")} testId="np-status-field">
                            <select data-testid="np-status" className={inputCls}
                                value={f.status} onChange={(e) => upd({ status: e.target.value })}>
                                <option value="active">{i18n.t("admin.l.active", "active")}</option>
                                <option value="draft">{i18n.t("admin.l.draft", "draft")}</option>
                                <option value="archived">{i18n.t("admin.l.archived", "archived")}</option>
                            </select>
                        </Field>
                    </div>
                </div>
                <DialogActions onClose={onClose}
                    canSubmit={canSubmit} busy={busy} onSubmit={submit}
                    submitLabel={i18n.t("admin.createPlan", "Create Plan")}
                    submitTestId="np-submit" />
            </DialogContent>
        </Dialog>
    );
}

// ── New Subscription ────────────────────────────────────────────
export function NewSubscriptionDialog({ onClose, onCreated }) {
    const [companies, setCompanies] = useState([]);
    const [f, setF] = useState({
        company_id: "", plan: "trial", status: "trial",
        trial_start: new Date().toISOString().slice(0, 10),
        trial_end: new Date(Date.now() + 30 * 86400_000).toISOString().slice(0, 10),
        renewal_date: "", billing_email: "", vat_number: "", notes: "",
    });
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        api.get("/admin/companies").then(({ data }) => setCompanies(data || []));
    }, []);
    const upd = (p) => setF((x) => ({ ...x, ...p }));
    const canSubmit = f.company_id && f.plan;
    const submit = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/subscriptions", f);
            toast.success(i18n.t("admin.l.Subscription_saved", "Subscription saved"));
            onCreated?.(data);
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally { setBusy(false); }
    };
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-xl" data-testid="new-subscription-dialog">
                <DialogHeader>
                    <DialogTitle>{i18n.t("admin.l.New_Subscription", "New Subscription")}</DialogTitle>
                    <DialogDescription>
                        {i18n.t("admin.l.Business_tracking_only_no_payment_processing", "Business tracking only — no payment processing.")}
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-3 md:grid-cols-2">
                    <div className="md:col-span-2">
                        <Field label={i18n.t("admin.l.Company", "Company")} required testId="ns-company-field">
                            <select data-testid="ns-company" className={inputCls}
                                value={f.company_id} onChange={(e) => upd({ company_id: e.target.value })}>
                                <option value="">{i18n.t("admin.l.Select_company", "— Select company —")}</option>
                                {companies.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </Field>
                    </div>
                    <Field label={i18n.t("admin.l.Plan", "Plan")} required testId="ns-plan-field">
                        <input data-testid="ns-plan" className={inputCls}
                            value={f.plan} onChange={(e) => upd({ plan: e.target.value })} />
                    </Field>
                    <Field label={i18n.t("admin.l.Status", "Status")} testId="ns-status-field">
                        <select data-testid="ns-status" className={inputCls}
                            value={f.status} onChange={(e) => upd({ status: e.target.value })}>
                            {["trial", "active", "pending_payment", "suspended", "cancelled", "manual_invoice", "expired"].map((s) => (
                                <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label={i18n.t("admin.l.Trial_start", "Trial start")} testId="ns-trial-start-field">
                        <input data-testid="ns-trial-start" type="date" className={inputCls}
                            value={f.trial_start} onChange={(e) => upd({ trial_start: e.target.value })} />
                    </Field>
                    <Field label={i18n.t("admin.l.Trial_end", "Trial end")} testId="ns-trial-end-field">
                        <input data-testid="ns-trial-end" type="date" className={inputCls}
                            value={f.trial_end} onChange={(e) => upd({ trial_end: e.target.value })} />
                    </Field>
                    <Field label={i18n.t("admin.l.Renewal_date", "Renewal date")} testId="ns-renewal-field">
                        <input data-testid="ns-renewal" type="date" className={inputCls}
                            value={f.renewal_date} onChange={(e) => upd({ renewal_date: e.target.value })} />
                    </Field>
                    <Field label={i18n.t("admin.l.Billing_email", "Billing email")} testId="ns-billing-field">
                        <input data-testid="ns-billing" type="email" className={inputCls}
                            value={f.billing_email} onChange={(e) => upd({ billing_email: e.target.value })} />
                    </Field>
                    <div className="md:col-span-2">
                        <Field label={i18n.t("admin.l.VAT_number", "VAT number")} testId="ns-vat-field">
                            <input data-testid="ns-vat" className={inputCls}
                                value={f.vat_number} onChange={(e) => upd({ vat_number: e.target.value })} />
                        </Field>
                    </div>
                    <div className="md:col-span-2">
                        <Field label={i18n.t("admin.l.Notes", "Notes")} testId="ns-notes-field">
                            <textarea data-testid="ns-notes" rows={2}
                                className={`${inputCls} h-auto py-2`}
                                value={f.notes} onChange={(e) => upd({ notes: e.target.value })} />
                        </Field>
                    </div>
                </div>
                <DialogActions onClose={onClose}
                    canSubmit={canSubmit} busy={busy} onSubmit={submit}
                    submitLabel={i18n.t("admin.saveSubscription", "Save Subscription")}
                    submitTestId="ns-submit" />
            </DialogContent>
        </Dialog>
    );
}

function DialogActions({ onClose, canSubmit, busy, onSubmit, submitLabel, submitTestId }) {
    return (
        <div className="mt-4 flex justify-end gap-2">
            <button
                data-testid="admin-form-cancel"
                onClick={onClose}
                className="h-10 px-4 rounded-md border border-neutral-300 text-sm">
                {i18n.t("admin.l.Cancel", "Cancel")}
            </button>
            <button
                data-testid={submitTestId}
                onClick={onSubmit}
                disabled={!canSubmit || busy}
                className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold disabled:opacity-40">
                {busy ? i18n.t("admin.l.Working", "Working…") : submitLabel}
            </button>
        </div>
    );
}
